import { Button, Modal, useToast } from '@citizen-walk/shared-ui';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  getModeTemplate,
  resolveMainSession,
  SESSIONS_FIELD_KEY,
  type DistrictMode,
  type FieldDef,
} from '@citizen-walk/shared-types';
import { usePhotoNotice, useOutbox } from '../posts';
import { useDistrictReady } from '../district';
import { useCurrentSession } from '../sessions';
import { useNickname } from './useNickname';
import { SessionPicker } from './SessionPicker';
import {
  UserFieldsForm,
  EMPTY_USER_FIELDS_VALUES,
  findMissingRequired,
  type UserFieldsFormValues,
} from './UserFieldsForm';
import { useMembershipUserFields } from './useMembershipUserFields';
import { loadRecoveryCode, persistRecoveryCode } from './recoveryCodeStorage';
import { useIssueRecoveryCode } from './useIssueRecoveryCode';
import {
  buildRecoveryQrLines,
  buildRecoveryUrl,
  generateQrDataUrl,
  recoveryQrSignature,
} from './qr';

export interface SettingsModalProps {
  open: boolean;
  onClose: () => void;
  /** 表示用 (地区名)。 */
  districtName?: string;
  /** 現在の uid。membership 読み書きに使用。 */
  uid?: string;
  /** 復帰コード QR の deep link 用 urlToken (なければコード文字列のみ QR 化)。 */
  districtToken?: string;
  /**
   * onboarding flag を reset するコールバック (任意)。渡されたとき
   * 「Onboarding を最初から見る」 button が表示される。
   */
  onResetOnboarding?: () => void;
}

/**
 * ユーザー設定モーダル (REQ-4.1.3、old2 入口フロー移植 Phase D)。
 *
 * 旧 old2 `apps/user/src/components/SettingsModal.tsx` を **逐語・完全同一** で移植:
 *   見出し『ユーザー設定』/ サブ『📍 {districtName} でのユーザー設定』、section 構成 =
 *   (a) 基本情報 = UserFieldsForm (district.dataSettings.userFields 駆動の動的フォーム)
 *   (b) 参加セッション = session checkbox で複数選択 (membership.joinedSessionIds)
 *   (c) 🔑 {districtName} のユーザー復帰コード = コード + コピー + QR(220px) + 紛失警告。
 *       **本画面をユーザー復帰コードの唯一の表示場所に一本化** (旧 Drawer『ユーザー復帰コードの表示』=
 *       RecoveryCodeModal は撤去)。コードは初回 (RecoveryIssuePage) に発行され localStorage
 *       (`cw.recoveryCode.v1.{districtId}`) に保存された値で、初回利用時に表示された値と同一。
 *       未保存時 (初回スキップ / 別端末復帰 / ブラウザデータ削除) は (c-1) メニューの
 *       『⌨ ユーザー復帰コードの入力』案内 + (c-2) 再発行ボタン (警告付き) を出す。
 *       ※ 復帰コードは平文がサーバに残らない設計のため、再発行すると過去に保存した旧コードは無効化される。
 *   (d) 保留中の投稿 (outbox) = 未送信/失敗件数 + 全件再送信 + オフライン時 alert
 *   (e) 写真撮影注意の表示をリセット (独立 underline button)
 *   保存 / 閉じるボタン。文言は old2 逐語。
 *
 * v2 調整:
 *   - shared-ui Modal + Button + useToast を shell に使用 (旧の inline overlay を Modal に統合)。
 *   - 配色を Tailwind preset semantic token (slate / primary / warning / danger) に置換。
 *   - 復帰コードは v2 localStorage (`cw.recoveryCode.v1.{districtId}`) から読込 (旧 IndexedDB meta)。
 *   - save() で gender/ageGroup/relationship/attribute を `value || null` 正規化 (old2 踏襲)。
 *   - nickname は localStorage (useNickname) と membership doc の両方に保存 (display 即時反映)。
 *
 * 参加セッションの保存方式 (本改修で変更):
 *   旧 v2 は old2 逐語踏襲で「参加セッションだけ即時 join/leave + その他は保存ボタン」という混在 UX
 *   だったが、利用者が「ユーザー設定で修正して保存する」一貫体験を求めたため、参加セッションも
 *   基本情報と同じ『保存』ボタンに統合 (draft state → save() で joinedSessionIds を一括書込)。
 *   これは onboarding (OnboardingPage) の「draft + 保存時 joinedSessionIds 一括保存」と同方式で、
 *   SessionPicker.tsx の注記「SettingsModal でのセッション変更は Save button pattern に従うべき」に沿う。
 *   ⇒ 即時 join/leave を行う useMembershipSessions への依存は SettingsModal からは撤去。
 */
export function SettingsModal({
  open,
  onClose,
  districtName,
  uid,
  districtToken,
  onResetOnboarding,
}: SettingsModalProps) {
  const { reset: resetPhotoNotice } = usePhotoNotice();
  const { toast } = useToast();

  const district = useDistrictReady();
  const resolvedToken = districtToken ?? district.urlToken;
  // docs/111 Wave 3 E-2/cross-3: nickname の localStorage は地区別キー (`cw.nickname.v1.{districtId}`)。
  // 以前は districtId なしで呼び phase2-default キーに読み書きしていたため、AppHome/MapPage が
  // 実 districtId キーを参照しても保存値が見えず、membership doc fallback 頼みになっていた。実 districtId を
  // 渡して保存先キーを一致させる (membership doc への save は従来どおり save() 内で別途行う)。
  const { nickname: localNickname, setNickname } = useNickname(district.id);

  // 地区別 userFields を動的レンダリング (admin 設定済 → dataSettings.userFields、未設定 → mode template)。
  // 【廃止予定 fallback】getModeTemplate (@deprecated) 経路は legacy 地区専用 (モードドリブン化
  // MD-4、2026-07-24) — 新規地区は ModeProfile 必須のため dataSettings.userFields が転写済み。
  // custom mode の legacy 地区が userFields 未設定だと BOUSAI_TEMPLATE に落ちる点に注意。
  const userFields: FieldDef[] = useMemo(() => {
    if (district.dataSettings?.userFields && district.dataSettings.userFields.length > 0) {
      return district.dataSettings.userFields;
    }
    if (district.dataSettings) {
      return getModeTemplate(district.mode as DistrictMode).dataSettings.userFields;
    }
    return [];
  }, [district.dataSettings, district.mode]);

  // 2026-09-05: 参加セッションの項目定義 (択一 / 複数・表示タイプ・項目名)。UserFieldsForm は
  // この項目だけ描画しない特殊扱いのため、SessionPicker に渡して設定を反映する。
  const sessionsField = useMemo(
    () => userFields.find((f) => f.key === SESSIONS_FIELD_KEY) ?? null,
    [userFields],
  );

  const membership = useMembershipUserFields(uid ?? null, district.id);
  // 表示名は参加登録を優先し、読めないときだけ端末内の保存値 (useResolvedNickname と同じ順、2026-10-09)。
  // 同じ人の別端末で変えた名前は onMembershipWrite で参加登録に届くので、端末内の古い名前を出さない。
  const nickname = membership.values.builtin.nickname || localNickname;
  const [values, setValues] = useState<UserFieldsFormValues>(EMPTY_USER_FIELDS_VALUES);
  const [saving, setSaving] = useState(false);

  // 参加セッション (複数選択 / 保存ボタンで一括反映)。
  // 旧 v2 は即時 join/leave だったが、基本情報と同じ「保存」ボタンに統合 (混在 UX を解消)。
  const { state: sessionState } = useCurrentSession(district.id);
  const sessions = sessionState.items;
  const [selectedSessions, setSelectedSessions] = useState<string[]>([]);
  // 案1: メインセッション (投稿時に post.sessionId へ付与)。参加が複数のとき選択 UI を出す。
  const [mainSessionDraft, setMainSessionDraft] = useState<string | null>(null);
  // 参加かつ active なセッション (表示順) = メインセッションの候補。
  const activeSelectedSessions = useMemo(
    () => sessions.filter((s) => selectedSessions.includes(s.id)),
    [sessions, selectedSessions],
  );
  // 有効メイン (参加 1 つなら自動 / draft が候補外なら先頭フォールバック)。表示・保存に使う。
  const effectiveMainDraft = useMemo(
    () =>
      resolveMainSession(
        selectedSessions,
        mainSessionDraft,
        sessions.map((s) => s.id),
      ),
    [selectedSessions, mainSessionDraft, sessions],
  );

  // ユーザー復帰コード + QR
  const [recoveryCode, setRecoveryCode] = useState<string | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  /**
   * QR 画像に焼き込んだニックネーム / 参加セッションの署名 (2026-09-04)。
   * 現在の保存値と食い違ったら「作り直しますか」を出すために保持する。
   */
  const [qrSignature, setQrSignature] = useState<string | null>(null);

  // 画像に焼き込む値は**保存済み**のもの (membership / localStorage) を使う。
  // 入力途中の draft で作り直しを促すと、保存前から「古い」と言われて混乱するため。
  const savedSessionNames = useMemo(
    () =>
      membership.joinedSessionIds
        .map((id) => sessions.find((s) => s.id === id)?.name)
        .filter((n): n is string => !!n),
    [membership.joinedSessionIds, sessions],
  );
  const currentQrSignature = useMemo(
    () => recoveryQrSignature(nickname, savedSessionNames),
    [nickname, savedSessionNames],
  );
  /**
   * 画像に焼き込む内容 (確定値)。コード確定時に一度だけ決め、以後は「作り直す」を押したときだけ更新する。
   * 現在値を直接使うと、ニックネームを変えた瞬間に画像が差し替わり「作り直しますか」を出す意味が無くなる。
   */
  const [qrContent, setQrContent] = useState<{ nickname: string; sessionNames: string[] } | null>(
    null,
  );
  /** 表示中の QR 画像が、保存済みのニックネーム / 参加セッションより古いか。 */
  const qrOutdated = !!qrDataUrl && qrSignature !== null && qrSignature !== currentQrSignature;
  const [codeCopied, setCodeCopied] = useState(false);
  // 再発行 (未保存時のみ): issueRecoveryCode callable を直接呼ぶ (旧 RecoveryCodeModal のロジックを統合)。
  const { state: issueState, call: issueCall } = useIssueRecoveryCode();

  // 保留中の投稿 (outbox)
  const { state: outboxState, flush } = useOutbox();
  const [retrying, setRetrying] = useState(false);

  // open のたびに membership 値 + 復帰コードをロード
  useEffect(() => {
    if (!open) return;
    setCodeCopied(false);
  }, [open]);

  // membership 読込完了で draft を作る。開いている間に 1 回だけ (参加登録は購読しているので、ほかの
  // 端末からの同期や自分の lastSeenAt 更新で値が届いても、編集中の入力を上書きしない、2026-10-09)。
  const draftInitialized = useRef(false);
  useEffect(() => {
    if (!open) {
      draftInitialized.current = false;
      return;
    }
    if (membership.status !== 'ready' || draftInitialized.current) return;
    draftInitialized.current = true;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setValues({
      ...membership.values,
      builtin: { ...membership.values.builtin, nickname },
    });
    setSelectedSessions(membership.joinedSessionIds);
    setMainSessionDraft(membership.mainSessionId);
  }, [
    open,
    membership.status,
    membership.values,
    membership.joinedSessionIds,
    membership.mainSessionId,
    nickname,
  ]);

  // 復帰コード読込 (地区別 namespace)。open / 地区変更で localStorage から再読込する。
  //
  // localStorage の保存キーは通常 `cw.recoveryCode.v1.{district.id}` だが、QR ディープリンク自動復帰
  // (RecoveryAutoPage) は「地区解決前の入口」で動くため district.id を持てず、`{urlToken}` キーに保存する。
  // そのため QR で復帰した端末の設定画面では district.id キーが空で「未保存」に見えてしまう不整合があった。
  // ここで urlToken キーにしか無い場合は district.id キーへ **遅延移送 (lazy migration)** し、以後の
  // 表示・参照を district.id に一本化する。urlToken キー側は RecoveryAutoPage の deep-link 再訪用の
  // 安全網として残す (削除しない)。URL / QR の内容は一切変えない (端末内ラベルの整合のみ)。
  useEffect(() => {
    if (!open || !district.id) return;
    let code = loadRecoveryCode(district.id);
    if (!code && resolvedToken && resolvedToken !== district.id) {
      const legacy = loadRecoveryCode(resolvedToken);
      if (legacy) {
        persistRecoveryCode(district.id, legacy);
        code = legacy;
      }
    }
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setRecoveryCode(code);
  }, [open, district.id, resolvedToken]);

  // recoveryCode (読込 or 再発行) が変わったら QR を再生成する。
  // 再発行 (issueState success → setRecoveryCode) も本 effect 経由で QR を更新できるよう、
  // QR 生成を localStorage 読込と分離した。
  // 焼き込む内容の確定 (2026-09-04)。membership と セッション一覧が揃ってから決める
  // (読込途中の空値で確定すると、直後に「変わりました」を誤表示してしまう)。
  useEffect(() => {
    if (!recoveryCode) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setQrContent(null);
      return;
    }
    if (membership.status !== 'ready' || sessionState.status !== 'ready') return;
    // 既に確定済みなら触らない (ニックネーム変更で勝手に作り直さないため)。
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setQrContent((prev) => prev ?? { nickname, sessionNames: savedSessionNames });
  }, [recoveryCode, membership.status, sessionState.status, nickname, savedSessionNames]);

  useEffect(() => {
    if (!recoveryCode || !qrContent) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setQrDataUrl(null);
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setQrSignature(null);
      return;
    }
    let cancelled = false;
    const qrInput = resolvedToken ? buildRecoveryUrl(resolvedToken, recoveryCode) : recoveryCode;
    // 2026-09-04: 画像にニックネームと参加セッションも焼き込む (QR が符号化する URL は不変)。
    void generateQrDataUrl(qrInput, {
      caption: districtName ? `📍 ${districtName}` : undefined,
      lines: buildRecoveryQrLines(qrContent.nickname, qrContent.sessionNames),
      subCaption: recoveryCode,
    }).then((url) => {
      if (cancelled) return;
      setQrDataUrl(url);
      setQrSignature(recoveryQrSignature(qrContent.nickname, qrContent.sessionNames));
    });
    return () => {
      cancelled = true;
    };
  }, [recoveryCode, resolvedToken, districtName, qrContent]);

  // 再発行 (issueRecoveryCode callable) の結果監視。
  // 成功時のみ localStorage に保存 + 表示更新 (旧コードはサーバ側で無効化済)。
  const issueStatus = issueState.status;
  const issuedCode = issueState.status === 'success' ? issueState.data.recoveryCode : null;
  const issueErrorMsg = issueState.status === 'error' ? issueState.error.message : null;
  useEffect(() => {
    if (issueStatus === 'success' && issuedCode && district.id) {
      persistRecoveryCode(district.id, issuedCode);
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setRecoveryCode(issuedCode);
      toast('success', 'ユーザー復帰コードを再発行しました');
    } else if (issueStatus === 'error') {
      toast('error', `発行に失敗しました: ${issueErrorMsg ?? '不明なエラー'}`);
    }
  }, [issueStatus, issuedCode, issueErrorMsg, district.id, toast]);

  async function handleReissue() {
    // 解釈B: コードは地区 (membership) 単位。再発行はこの地区のコードのみ上書きし、他地区は影響しない。
    // 当該地区の旧コードは平文がサーバに残らないため、再発行すると過去に保存した旧コードは無効化される。
    if (!district.id) return;
    if (
      !window.confirm(
        '再発行すると、この地区の以前のユーザー復帰コードは使えなくなります。\nコードを紛失した場合のみ実行してください。続けますか?',
      )
    ) {
      return;
    }
    await issueCall(district.id);
  }

  const missing = useMemo(() => findMissingRequired(userFields, values), [userFields, values]);

  async function save() {
    if (!uid || !district.id) return;
    setSaving(true);
    try {
      const trimmedNickname = values.builtin.nickname.trim();
      // 1) localStorage の nickname を更新 (display で広く使われているため即時反映)
      setNickname(trimmedNickname);
      // 2) membership doc に保存 — gender/ageGroup/relationship/attribute の `value || null` 正規化は
      //    useMembershipUserFields.save 内で行う (old2 `value || null` 踏襲)。
      //    参加セッション (selectedSessions) も第2引数で同時に joinedSessionIds へ一括保存する。
      await membership.save(
        {
          builtin: { ...values.builtin, nickname: trimmedNickname },
          custom: values.custom,
        },
        selectedSessions,
        // 案1: 有効メイン (参加1つなら自動的にそれ / 候補外なら先頭) を明示保存。
        effectiveMainDraft,
      );
      onClose();
    } catch (e) {
      console.error(e);
      toast('error', '保存に失敗しました');
    } finally {
      setSaving(false);
    }
  }

  async function handleResetPhotoNotice() {
    resetPhotoNotice();
    toast('info', '次回の撮影時に注意喚起を再表示します');
  }

  async function retryAllOutbox() {
    if (retrying) return;
    if (!navigator.onLine) {
      toast('error', 'オフラインのため再送できません。電波の良い場所で再度お試しください。');
      return;
    }
    setRetrying(true);
    try {
      const result = await flush();
      if (result.failed > 0) {
        toast('error', `${result.sent} 件送信成功 / ${result.failed} 件まだ失敗しています`);
      } else if (result.sent > 0) {
        toast('success', `${result.sent} 件すべて送信しました`);
      }
    } finally {
      setRetrying(false);
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="ユーザー設定" size="md">
      {districtName && (
        <p className="text-sm text-slate-600 mb-4">📍 {districtName} でのユーザー設定</p>
      )}

      <Section title="基本情報">
        <UserFieldsForm
          fields={userFields}
          values={values}
          onChange={setValues}
          disabled={saving || membership.status === 'loading'}
        />
        {missing.length > 0 && (
          <p className="text-xs text-danger-700 mt-1">未入力の必須項目: {missing.join('、')}</p>
        )}
      </Section>

      {sessions.length > 0 && (
        // 2026-09-05: 見出しと入力形式は地区の `sessions` 項目定義 (項目名 / 択一・複数 / 表示タイプ) に従う。
        // 以前はここが「参加セッション」+ チェックボックス固定で、管理画面の設定が反映されなかった。
        <Section title={sessionsField?.label?.trim() || '参加セッション'}>
          <SessionPicker
            sessions={sessions}
            field={sessionsField}
            value={selectedSessions}
            onChange={setSelectedSessions}
            disabled={saving}
          />
          <p className="text-xs text-slate-500 mt-1">「保存」で反映されます。</p>

          {/* 案1: メインセッション選択。参加が複数のときだけ表示 (1つなら自動)。 */}
          {activeSelectedSessions.length >= 2 ? (
            <div className="mt-3 pt-3 border-t border-slate-200">
              <label className="block text-sm font-medium mb-1" htmlFor="main-session-select">
                メインセッション（投稿に使う）
              </label>
              <p className="text-xs text-slate-500 mb-1">
                投稿したときに、この投稿がどのセッションのものかを記録するために使います。複数参加しているので、ふだん投稿に使うセッションを1つ選んでください。
              </p>
              <select
                id="main-session-select"
                value={effectiveMainDraft ?? ''}
                onChange={(e) => setMainSessionDraft(e.target.value || null)}
                disabled={saving}
                className="min-h-touch w-full px-3 border border-slate-300 rounded bg-white text-sm"
              >
                {activeSelectedSessions.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
          ) : activeSelectedSessions.length === 1 ? (
            <p className="mt-2 text-xs text-slate-500">
              メインセッション:{' '}
              <span className="font-medium">{activeSelectedSessions[0]!.name}</span>（自動）
            </p>
          ) : null}
        </Section>
      )}

      <Section
        title={districtName ? `🔑 ${districtName} のユーザー復帰コード` : '🔑 ユーザー復帰コード'}
      >
        {recoveryCode ? (
          <div>
            <div className="bg-slate-100 p-3 rounded text-center mb-3">
              <code className="text-lg font-mono select-all tracking-wider">{recoveryCode}</code>
            </div>
            <button
              type="button"
              onClick={() => {
                void navigator.clipboard.writeText(recoveryCode);
                setCodeCopied(true);
                setTimeout(() => setCodeCopied(false), 2000);
              }}
              className="w-full text-sm py-2 border border-slate-300 rounded hover:bg-slate-50 mb-3"
            >
              {codeCopied ? 'コピーしました' : 'コードをコピー'}
            </button>
            {qrDataUrl && (
              <div className="text-center">
                <img
                  src={qrDataUrl}
                  alt="ユーザー復帰コードQR"
                  className="mx-auto mb-2 w-[220px]"
                />
                <p className="text-xs text-slate-500">
                  QRコードを読み取ると自動復帰できます。画像にはニックネームと参加セッションも入っています。
                </p>
              </div>
            )}
            {/* 2026-09-04: 名前や参加セッションを変えると画像の記載が古くなる。
                QR (復帰先) は変わらないので自動では作り直さず、本人に確認してから作り直す。 */}
            {qrOutdated && (
              <div className="mt-2 border border-warning-600 bg-warning-50 rounded p-2 text-left">
                <p className="text-xs text-warning-800">
                  ニックネームまたは参加セッションが変わりました。QR画像に書かれている内容は変更前のままです。作り直しますか？
                  <span className="block mt-0.5 text-warning-700">
                    ※ 読み取り先は変わらないので、保存済みの画像もそのまま使えます。
                  </span>
                </p>
                <button
                  type="button"
                  onClick={() => setQrContent({ nickname, sessionNames: savedSessionNames })}
                  className="mt-2 w-full text-sm py-2 border border-warning-600 text-warning-800 rounded hover:bg-warning-100"
                >
                  QR画像を作り直す
                </button>
              </div>
            )}
            <p className="text-xs text-warning-600 mt-2">
              このコードを紛失すると、別の端末やブラウザからの復帰ができなくなります。スクリーンショットで保存することをお勧めします。
            </p>
            {/* このコード/QR で復帰できない (無効・古い) 場合の再発行導線。表示中コードと
                サーバ側コードが食い違ったときでも、ここから作り直せるよう常設する。 */}
            <p className="text-xs text-slate-500 mt-3">
              このコード/QR で復帰できない場合は、再発行して新しいコードを作成してください。
            </p>
            <button
              type="button"
              onClick={() => void handleReissue()}
              disabled={issueState.status === 'pending'}
              className="w-full text-sm py-2 border border-warning-600 text-warning-700 rounded hover:bg-warning-50 disabled:opacity-40 mt-1"
            >
              {issueState.status === 'pending' ? '発行中…' : 'ユーザー復帰コードを再発行する'}
            </button>
          </div>
        ) : (
          <div className="grid gap-2">
            <p className="text-sm text-slate-600">
              このブラウザにはユーザー復帰コードが保存されていません。
            </p>
            {/* (c-1) 既にコードを持っている人向け: メニューの「ユーザー復帰コードの入力」へ誘導。
                復帰 (入力) はコードの照合のみで再発行を伴わないため、旧コードは無効化されない。 */}
            <p className="text-xs text-slate-500">
              すでにユーザー復帰コードをお持ちの場合（別の端末や紙などに保存済み）は、メニュー（☰）の「⌨
              ユーザー復帰コードの入力」から復帰できます（このとき旧コードは無効化されません）。
            </p>
            {/* (c-2) コードを紛失した人向け: 再発行 (= 旧コード無効化) を警告付きで提供。 */}
            <p className="text-xs text-warning-600">
              ⚠
              コードを紛失した場合のみ再発行してください。再発行すると、以前に保存したコードは使えなくなります。
            </p>
            <button
              type="button"
              onClick={() => void handleReissue()}
              disabled={issueState.status === 'pending'}
              className="w-full text-sm py-2 border border-warning-600 text-warning-700 rounded hover:bg-warning-50 disabled:opacity-40"
            >
              {issueState.status === 'pending' ? '発行中…' : 'ユーザー復帰コードを再発行する'}
            </button>
          </div>
        )}
      </Section>

      {(outboxState.pendingCount > 0 || outboxState.failedCount > 0) && (
        <Section title="保留中の投稿">
          <div className="text-sm text-slate-700 grid gap-1">
            {outboxState.pendingCount > 0 && (
              <div>
                ⏳ 未送信: <strong>{outboxState.pendingCount} 件</strong>
                （オンラインで自動送信されます）
              </div>
            )}
            {outboxState.failedCount > 0 && (
              <div>
                ⚠️ 送信失敗: <strong>{outboxState.failedCount} 件</strong>
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={() => void retryAllOutbox()}
            disabled={retrying}
            className="w-full mt-2 py-2 text-sm border border-warning-600 text-warning-700 rounded hover:bg-warning-50 disabled:opacity-40"
          >
            {retrying ? '再送信中…' : '全件再送信する'}
          </button>
        </Section>
      )}

      {onResetOnboarding && (
        <Section title="ガイド">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              onResetOnboarding();
              onClose();
            }}
          >
            Onboarding を最初から見る
          </Button>
        </Section>
      )}

      <button
        type="button"
        onClick={() => void save()}
        disabled={saving}
        className="w-full bg-primary-600 text-white py-3 rounded font-medium disabled:opacity-40 mt-4"
      >
        {saving ? '保存中…' : '保存'}
      </button>

      <button
        type="button"
        onClick={() => void handleResetPhotoNotice()}
        className="w-full mt-3 text-sm text-slate-700 underline"
      >
        写真撮影注意の表示をリセット
      </button>

      <button type="button" onClick={onClose} className="w-full mt-3 text-slate-700 py-3">
        閉じる
      </button>
    </Modal>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="mb-4">
      <h3 className="font-medium text-sm text-slate-500 mb-2">{title}</h3>
      <div className="grid gap-3">{children}</div>
    </div>
  );
}
